import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, Group } from '../../../models';
import { IGroupService, IGroupSelectionService } from '../../../services';
import { groupSelected, treeViewNavigationEnd, treeViewNavigationStart } from '../../../common';
import { buildGroupList } from '../../dashboards/modernDashboardContextual/buildGroupList';

class GroupByTypeController implements angular.IComponentController {
    currentGroup: Group;
    isLoading: boolean;
    loadingMessage: string;
    previousGroup: Group;
    nextGroup: Group;
    groups: Group[];
    allGroups: Group[];
    types: string[];
    selectedGroupIndex: number;

    private _garbages: (() => void)[] = [];
    readonly groupGuids: string[];
    readonly selectedGroup: Group;

    static $inject = [
        '$rootScope',
        'groupService',
        'groupSelectionService'
    ];

    constructor(
        private _$rootScope: angular.IRootScopeService,
        private _groupService: IGroupService,
        private _groupSelectionService: IGroupSelectionService
    ) {
    }

    $onInit(): void {
        this._garbages.push(
            this._$rootScope.$on(treeViewNavigationStart, () => {
                this.isLoading = true;
            }),
            this._$rootScope.$on(treeViewNavigationEnd, () => {
                this.isLoading = false;
            })
        );
    }

    $onChanges(changesObj: angular.IOnChangesObject): void {
        if (changesObj['allGroups']?.currentValue) {
            this.refreshGroupList();
        }
        if (changesObj['groupGuids']?.currentValue) {
            this.refreshGroupList();
        }
        if (changesObj['selectedGroup']?.currentValue) {
            this.select(changesObj['selectedGroup']?.currentValue);
        }
    }

    $onDestroy(): void {
        this._garbages.forEach(g => g());
    }

    select(selectedGroup?: Group): void {
        if (!this.currentGroup) {
            this.currentGroup = selectedGroup;
        }
        if (selectedGroup && selectedGroup === this.selectedGroup) {
            // already selected
            return;
        }
        this.selectedGroupIndex = this.findIndex(this.groups, selectedGroup);
        // Fallback: if group not found, use the first in the list
        if (this.selectedGroupIndex < 0 && this.groups) {
            selectedGroup = this.groups[0];
        }
        if (selectedGroup) {
            this._groupSelectionService.selectedGroup(selectedGroup);
            this._$rootScope.$broadcast(groupSelected, selectedGroup, true);
            this.selectedGroupIndex = this.findIndex(this.groups, selectedGroup);
            this.setPreviousGroup(this.groups, selectedGroup);
            this.setNextGroup(this.groups, selectedGroup);
            this.currentGroup = selectedGroup;
        }
    }

    next(): void {
        if (this.selectedGroup && this.selectedGroup.guid) {
            const selectedGroup = this.getNextGroup(this.groups, this.selectedGroup);
            this.currentGroup = selectedGroup;
            this._$rootScope.$broadcast(groupSelected, selectedGroup, true);
        }
    }

    previous(): void {
        if (this.selectedGroup && this.selectedGroup.guid) {
            const selectedGroup = this.getPreviousGroup(this.groups, this.selectedGroup);
            this.currentGroup = selectedGroup;
            this._$rootScope.$broadcast(groupSelected, selectedGroup, true);
        }
    }

    private refreshGroupList(): angular.IPromise<void> {
        const groups = buildGroupList({ types: this.types, groupGuids: this.groupGuids }, this.allGroups);
        this.groups = groups
            .map(d => d.group);
        // update selected from existing object in list
        if (this.selectedGroup) {
            this.selectByGuid(this.selectedGroup.guid);
        }

        return this._groupService
            .getPaths(this.groups.map(d => d.guid))
            .then(data => {
                this.groups.forEach(d => {
                    const path = _.find(data, dd => dd.guid === d.guid);
                    if (path) {
                        (d as any).absolutePath = path.path;
                    }
                });
                if (!this.selectedGroup) {
                    const selected = this._groupSelectionService.selectedGroup();
                    if (selected) {
                        this.selectByGuid(selected.guid);
                    }
                }
                if (!this.selectedGroup) {
                    this.select(this.groups[0]);
                }
            });
    }

    private selectByGuid(guid: string): void {
        const found = _.find(this.groups, g => g.guid === guid);
        if (found) {
            this.select(found);
        }
    }

    private findIndex(groups: Group[], selectedGroup: Group): number {
        if (groups && selectedGroup && selectedGroup.guid) {
            const indexes = groups.map((group, index) => {
                if (group.guid === selectedGroup.guid) {
                    return index;
                }
            }).filter(isFinite);

            if (indexes && indexes.length === 1) {
                return indexes[0];
            }
        }
        return -1;
    }

    private getPreviousGroup(groups: Group[], selectedGroup: Group): Group {
        if (selectedGroup && selectedGroup.guid) {
            let selectedGroupIndex = this.findIndex(groups, selectedGroup);
            if (selectedGroupIndex === 0) {
                selectedGroupIndex = groups.length - 1;
            } else {
                selectedGroupIndex--;
            }
            if (selectedGroupIndex >= 0 && selectedGroupIndex < groups.length) {
                return groups[selectedGroupIndex];
            }
        }
        return null;
    }

    private getNextGroup(groups: Group[], selectedGroup: Group): Group {
        if (selectedGroup && selectedGroup.guid) {
            let selectedGroupIndex = this.findIndex(groups, selectedGroup);
            if (groups && selectedGroupIndex === groups.length - 1) {
                selectedGroupIndex = 0;
            } else {
                selectedGroupIndex++;
            }
            if (groups && selectedGroupIndex >= 0 && selectedGroupIndex < groups.length) {
                return groups[selectedGroupIndex];
            }
        }
        return null;
    }

    private setPreviousGroup(groups: Group[], selectedGroup: Group): void {
        if (selectedGroup && selectedGroup.guid) {
            this.previousGroup = this.getPreviousGroup(groups, selectedGroup);
        }
    }

    private setNextGroup(groups: Group[], selectedGroup: Group): void {
        if (selectedGroup && selectedGroup.guid) {
            this.nextGroup = this.getNextGroup(groups, selectedGroup);
        }
    }
}

export const wtGroupByType: ComponentDefinition = {
    componentId: 'wtGroupByType',
    controller: GroupByTypeController,
    template: require('./groupByType.component.html'),
    controllerAs: 'vm',
    bindings: {
        allGroups: '<',
        groupGuids: '<?',
        selectedGroup: '<',
        types: '='
    }
};